import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IonContent, IonFooter } from '@ionic/react';

import intl from '$intl';
import { screen } from '$fcomponents/hoc';
import { Div, SPACE } from '$gstyles';
import Header from '$components/header';
import CurrentStateModel from '$fbusiness/models/currentState';
import { apiService } from '$gbusiness/services';
import { dialog, Modal } from '$gcomponents/reusables';
import { Button } from '$gcomponents/primitives';
import { Checkbox } from '@mui/material';
import { Flex } from '$gstyles/wrapper';

import { IonPageWrapper } from './styles';

interface SuperadminModel {
  id: number;
  name: string;
  email: string;
  access_type: 'all' | 'restricted';
  allowed_factories: Array<{ id: number; name: string }>;
}

interface FactoryModel {
  id: number;
  name: string;
}

interface SuperadminAccessScreenProps {
  history: any;
  currentState: CurrentStateModel;
}

const SuperadminAccessScreen: React.FC<SuperadminAccessScreenProps> = ({ currentState }) => {
  const [superadmins, setSuperadmins] = useState<SuperadminModel[]>([]);
  const [allFactories, setAllFactories] = useState<FactoryModel[]>([]);
  const [selectedSuperadmin, setSelectedSuperadmin] = useState<SuperadminModel | null>(null);
  const [selectedFactoryIds, setSelectedFactoryIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [superadminsRes, factoriesRes] = await Promise.all([
        apiService.fetchApi({
          url: 'admin/superadmins',
          method: 'GET',
        }),
        apiService.fetchApi({
          url: 'admin/factories/all',
          method: 'GET',
        }),
      ]);

      if (superadminsRes?.success) {
        setSuperadmins(superadminsRes.superadmins || []);
      }
      if (factoriesRes?.success) {
        setAllFactories(factoriesRes.factories || []);
      }
    } catch (error) {
      console.error('Failed to fetch superadmin data:', error);
    }
    setLoading(false);
  };

  const openEditModal = (superadmin: SuperadminModel) => {
    setSelectedSuperadmin(superadmin);
    setSelectedFactoryIds(
      superadmin.access_type === 'all' ? [] : superadmin.allowed_factories.map((f) => f.id)
    );
  };

  const closeModal = () => {
    setSelectedSuperadmin(null);
    setSelectedFactoryIds([]);
  };

  const toggleFactory = (factoryId: number) => {
    setSelectedFactoryIds((prev) =>
      prev.includes(factoryId) ? prev.filter((id) => id !== factoryId) : [...prev, factoryId]
    );
  };

  const selectAll = () => {
    setSelectedFactoryIds(allFactories.map((f) => f.id));
  };

  const clearAll = () => {
    setSelectedFactoryIds([]);
  };

  const saveAccess = async () => {
    if (!selectedSuperadmin) return;

    const response = await apiService.fetchApi({
      url: `admin/superadmins/${selectedSuperadmin.id}/factories`,
      method: 'PUT',
      param: { factory_ids: selectedFactoryIds },
    });

    if (response?.success) {
      dialog.alert({
        title: intl('MESSAGE.SUCCESS'),
        message: intl('MESSAGE.SAVED_SUCCESSFULLY'),
      });
      closeModal();
      fetchData();
    } else {
      dialog.alert({
        title: intl('MESSAGE.ERROR'),
        message: response?.message || intl('MESSAGE.SAVE_FAILED'),
      });
    }
  };

  const grantAllAccess = async () => {
    if (!selectedSuperadmin) return;

    dialog.confirm({
      message: 'Grant this superadmin access to ALL factories? This removes all restrictions.',
      onPress: async () => {
        const response = await apiService.fetchApi({
          url: `admin/superadmins/${selectedSuperadmin.id}/factories`,
          method: 'PUT',
          param: { factory_ids: [] },
        });

        if (response?.success) {
          closeModal();
          fetchData();
        }
      },
    });
  };

  // Only super superadmins can access this screen
  if (!currentState.isSuperSuperAdmin) {
    return (
      <IonPageWrapper>
        <Header titleText="Superadmin Factory Access" />
        <IonContent>
          <Div className="empty-state">
            <p>You do not have permission to access this page.</p>
            <p>Only superadmins with unrestricted access can manage other superadmins.</p>
          </Div>
        </IonContent>
      </IonPageWrapper>
    );
  }

  return (
    <IonPageWrapper>
      <Header titleText="Superadmin Factory Access" />
      <IonContent>
        <Div maxWidth="800px" className="container" padding={SPACE.LARGE}>
          {loading ? (
            <Div className="empty-state">Loading...</Div>
          ) : superadmins.length === 0 ? (
            <Div className="empty-state">No superadmins found.</Div>
          ) : (
            superadmins.map((superadmin) => (
              <Div key={superadmin.id} className="superadmin-card">
                <Div className="superadmin-header">
                  <span className="superadmin-name">{superadmin.name}</span>
                  <span
                    className={`access-badge ${
                      superadmin.access_type === 'all' ? 'all-access' : 'restricted'
                    }`}>
                    {superadmin.access_type === 'all' ? 'All Factories' : 'Restricted'}
                  </span>
                </Div>
                <Div className="superadmin-email">{superadmin.email}</Div>
                {superadmin.access_type === 'restricted' && (
                  <Div className="factory-chips">
                    {superadmin.allowed_factories.map((factory) => (
                      <span key={factory.id} className="factory-chip">
                        {factory.name}
                      </span>
                    ))}
                  </Div>
                )}
                <Flex justifyContent="flex-end" marginTop={SPACE.MEDIUM}>
                  <Button variant="outlined" size="small" onClick={() => openEditModal(superadmin)}>
                    {intl('BUTTON.EDIT')}
                  </Button>
                </Flex>
              </Div>
            ))
          )}
        </Div>

        {/* Edit Modal */}
        <Modal
          titleText={selectedSuperadmin ? `Edit Access: ${selectedSuperadmin.name}` : ''}
          show={!!selectedSuperadmin}
          onClose={closeModal}
          useCustom>
          {selectedSuperadmin && (
            <>
              <IonContent>
                <Div className="modal-content">
                  <Flex justifyContent="space-between" marginBottom={SPACE.MEDIUM}>
                    <Div>
                      <Button variant="text" size="small" onClick={selectAll}>
                        Select All
                      </Button>
                      <Button variant="text" size="small" onClick={clearAll}>
                        Clear All
                      </Button>
                    </Div>
                    <Button variant="text" size="small" color="success" onClick={grantAllAccess}>
                      Grant All Access
                    </Button>
                  </Flex>

                  <Div className="factory-list">
                    {allFactories.map((factory) => (
                      <Div key={factory.id} className="factory-item">
                        <Checkbox
                          checked={selectedFactoryIds.includes(factory.id)}
                          onChange={() => toggleFactory(factory.id)}
                        />
                        <label onClick={() => toggleFactory(factory.id)}>{factory.name}</label>
                      </Div>
                    ))}
                  </Div>

                  {selectedFactoryIds.length === 0 && (
                    <Div
                      style={{ marginTop: SPACE.MEDIUM, fontSize: '0.9em', color: 'var(--ion-color-success)' }}>
                      Note: Saving with no factories selected will grant access to ALL factories.
                    </Div>
                  )}
                </Div>
              </IonContent>
              <IonFooter>
                <Flex padding={SPACE.MEDIUM} justifyContent="space-between">
                  <Button variant="outlined" onClick={closeModal}>
                    {intl('BUTTON.CANCEL')}
                  </Button>
                  <Button variant="contained" color="primary" onClick={saveAccess}>
                    {intl('BUTTON.SAVE')}
                  </Button>
                </Flex>
              </IonFooter>
            </>
          )}
        </Modal>
      </IonContent>
    </IonPageWrapper>
  );
};

const mapStateToProps = (state) => ({
  resetOnRoute: true,
});

const mapDispatchToProps = {};

const connected = connect(mapStateToProps, mapDispatchToProps);

export default connected(screen(SuperadminAccessScreen));
